/** @jsx jsx */
import { jsx } from "@emotion/core";
import { useState, useMemo, useEffect } from "react";
import { ContainerTreeBase } from "..";
import { getOrderedItems, getFirstItemToSetFocus, } from "../Utils/ContainerUtil";
import { ContainerRefs } from "../Utils/ContainerRefs";
import { getComponents } from "./getComponents";
import { UniqueContainerSet } from "../UniqueContainerId/UniqueContainerSet";
import { UniqueContainerId } from "../UniqueContainerId/UniqueContainerId";
import { TestId } from "./TestId";
import { DEFAULT_TREE_CHILD_INDENT } from "./constants";
export var ContainerTree = function (_a) {
    var data = _a.data, componentOverrides = _a.componentOverrides, onClick = _a.onClick, onContextMenu = _a.onContextMenu, onMouseEnter = _a.onMouseEnter, onMouseLeave = _a.onMouseLeave, onExpandClick = _a.onExpandClick, onCursorMove = _a.onCursorMove, onTabOut = _a.onTabOut, onShiftTabOut = _a.onShiftTabOut, onEnter = _a.onEnter, onSpace = _a.onSpace, overridingSelectedSet = _a.selectedSet, initialFocusItem = _a.initialFocusItem, overridingExpandSet = _a.expandedSet, overScanSize = _a.overScanSize, _b = _a.indentPerTreeLevel, indentPerTreeLevel = _b === void 0 ? DEFAULT_TREE_CHILD_INDENT : _b;
    var _c = useState(new UniqueContainerSet()), expandedSet = _c[0], setExpandedSet = _c[1];
    var selectedSet = overridingSelectedSet || new UniqueContainerSet();
    var orderedItems = useMemo(function () {
        return getOrderedItems(data, overridingExpandSet || expandedSet);
    }, [data, overridingExpandSet, expandedSet]);
    var refItems = useMemo(function () {
        return new ContainerRefs(data);
    }, [data]);
    var _d = useState(getFirstItemToSetFocus(orderedItems, selectedSet, initialFocusItem)), currentlyFocussedItemIndex = _d[0], setCurrentlyFocussedItemIndex = _d[1];
    useEffect(function () {
        setCurrentlyFocussedItemIndex(getFirstItemToSetFocus(orderedItems, selectedSet, initialFocusItem));
    }, [initialFocusItem]);
    var toggleExpand = function (containerData, event) {
        event.stopPropagation();
        if (onExpandClick) {
            onExpandClick(containerData, event);
        }
        else {
            setExpandedSet(function (prevState) {
                var copySet = new UniqueContainerSet(prevState);
                if (prevState.has(containerData)) {
                    copySet["delete"](containerData);
                }
                else {
                    copySet.add(containerData);
                }
                return copySet;
            });
        }
    };
    var handleClick = function (containerData, event) {
        if (onClick) {
            onClick(containerData, event);
        }
        else {
            toggleExpand(containerData, event);
        }
    };
    var overrides = getComponents(componentOverrides);
    var handleKeyDown = function (event) {
        var currentlyFocussedItemData = orderedItems[currentlyFocussedItemIndex];
        switch (event.key) {
            case "Tab":
                /**
                 * We should not prevent the default tab behavior, as we have implemented
                 * roving tab index to manage focus within tree,
                 * https://www.w3.org/WAI/ARIA/apg/practices/keyboard-interface/#kbd_roving_tabindex
                 */
                if (event.shiftKey) {
                    if (onShiftTabOut) {
                        onShiftTabOut();
                    }
                }
                else if (onTabOut) {
                    onTabOut();
                }
                break;
            case "Enter":
                if (onEnter) {
                    event.preventDefault();
                    onEnter(currentlyFocussedItemData, event);
                }
                break;
            case " ":
                if (onSpace) {
                    event.preventDefault();
                    onSpace(currentlyFocussedItemData, event);
                }
                break;
            case "ArrowLeft":
                event.preventDefault();
                // Left pressed - if we are on a folder or worksheet close that folder/worksheet
                if (onExpandClick) {
                    onExpandClick(currentlyFocussedItemData, event);
                }
                else {
                    setExpandedSet(function (prevState) {
                        var copySet = new UniqueContainerSet(prevState);
                        if (prevState.has(currentlyFocussedItemData)) {
                            copySet["delete"](currentlyFocussedItemData);
                        }
                        return copySet;
                    });
                }
                break;
            case "ArrowRight":
                event.preventDefault();
                // Right pressed - if we are on a folder or worksheet open that folder/worksheet
                if (onExpandClick) {
                    onExpandClick(currentlyFocussedItemData, event);
                }
                else {
                    setExpandedSet(function (prevState) {
                        var copySet = new UniqueContainerSet(prevState);
                        if (!prevState.has(currentlyFocussedItemData)) {
                            copySet.add(currentlyFocussedItemData);
                        }
                        return copySet;
                    });
                }
                break;
            case "ArrowUp":
                event.preventDefault();
                // Up pressed - move cursor to previous container, or to parent if none is available, or to null
                if (currentlyFocussedItemIndex > 0) {
                    setFocusByIndex(currentlyFocussedItemIndex - 1, event);
                }
                break;
            case "ArrowDown":
                event.preventDefault();
                // Down pressed - move cursor to next container, or to the container after the parent, or to null
                if (currentlyFocussedItemIndex + 1 < orderedItems.length) {
                    setFocusByIndex(currentlyFocussedItemIndex + 1, event);
                }
        }
    };
    var setFocusByIndex = function (indexOfItemToFocus, event) {
        updateCursor(indexOfItemToFocus, event);
        var currentlyFocussedItemData = orderedItems[indexOfItemToFocus];
        if (currentlyFocussedItemData === null || currentlyFocussedItemData === void 0 ? void 0 : currentlyFocussedItemData.id) {
            var activeRef = refItems.get(UniqueContainerId.fromContainerTreeData(currentlyFocussedItemData));
            /**
             * In the first time rendering when initialFocusItem is passed
             * the passed item may be below the the current scroll view and may not be
             * available in the DOM due to virtualization.
             * But for future scenarios when we are using keyboard to move to items 1 up or down,
             * This is helpful to set focus on the li list items
             * */
            if (activeRef === null || activeRef === void 0 ? void 0 : activeRef.current) {
                activeRef.current.focus();
            }
        }
    };
    var updateCursor = function (indexOfNewlyFocussedItem, event) {
        setCurrentlyFocussedItemIndex(indexOfNewlyFocussedItem);
        if (onCursorMove && event) {
            onCursorMove(orderedItems[indexOfNewlyFocussedItem], event);
        }
    };
    return (jsx("div", { "data-testid": TestId.OUTER_DIV, onKeyDown: handleKeyDown, css: {
            display: "flex",
            flexDirection: "column",
            height: "100%"
        } },
        jsx(ContainerTreeBase, { data: orderedItems, onClick: handleClick, onContextMenu: onContextMenu, onMouseEnter: onMouseEnter, onMouseLeave: onMouseLeave, onExpandClick: toggleExpand, expandedSet: overridingExpandSet || expandedSet, componentOverrides: overrides, selectedSet: selectedSet, lastFocussedItemIndex: currentlyFocussedItemIndex, refItems: refItems, updateCursor: setFocusByIndex, setFocus: setFocusByIndex, indentPerTreeLevel: indentPerTreeLevel, overScanSize: overScanSize })));
};
//# sourceMappingURL=ContainerTree.js.map